import { pick, without } from 'lodash-es';
import pRetry, { type FailedAttemptError } from 'p-retry';
import { type StateCreator } from 'zustand';
import {
  isAudioWorkEnabled,
  runTrackedAudioWork,
  swallowAudioWorkCutoff,
  trackDetachedAudioWork,
  type AudioWorkContext,
} from '../audioWorkBarrier';
import { db } from '../db';
import { getSafeErrorFields, isSessionNotFoundError } from '../errors';
import { getPipelineMode } from '../persistenceV2';
import { type RecordingKey } from '../persistenceV2/types';
import { type Chunk, type SessionChunk, type SessionRecordingStore } from '../types';
import { useAudioGraphStore } from '../useAudioGraphStore';
import { getErrorInfo } from '../utils';
import { ChunkRecorder } from './lib/ChunkRecorder';
import { getChunkInfo } from './lib/getChunkInfo';
import { logger } from './lib/logger';
import { releaseAnalyzerId } from './lib/releaseAnalyzerId';
import { simpleRetry } from './simpleRetry';
import {
  discardCaptureSlotV2,
  startCaptureV2,
  stopCaptureV2,
} from './v2/captureLifecycleV2';
import { type ChunkCaptureRecorder } from './v2/ChunkCaptureRecorder';
import {
  getIsLastChunkV2,
  processChunkV2,
  type ChunkDispatchOutcome,
  type ChunkDispatchSource,
} from './v2/processChunkV2';

export interface ChunkSlice {
  processingChunksIds: string[];
  chunkRecorders: Record<string, ChunkRecorder>;
  /** The single live v2 capture for this page. */
  captureSlot: { recordingId: string; recorder: ChunkCaptureRecorder } | null;
  /** `deduped` only on the ledger pipeline; the legacy path reports nothing. */
  processChunk: (
    chunk: SessionChunk,
    useTranscriptionServiceV2: boolean,
    context?: AudioWorkContext,
    source?: ChunkDispatchSource
  ) => Promise<ChunkDispatchOutcome | void>;
  startChunkRecorder: (
    stream: MediaStream,
    useTranscriptionServiceV2: boolean,
    recordingKey: RecordingKey
  ) => void;
  stopChunkRecorder: (recordingId?: string) => Promise<void>;
  discardChunkRecorder: (recordingId: string) => void;
  getIsLastChunk: (chunk: SessionChunk) => Promise<boolean>;
}

export const createChunkSlice: StateCreator<SessionRecordingStore, [], [], ChunkSlice> = (
  set,
  get
) => ({
  chunkRecorders: {},
  captureSlot: null,
  processingChunksIds: [],

  processChunk: async (chunk, useTranscriptionServiceV2, context, source) => {
    if (getPipelineMode() === 'ledger') {
      return processChunkV2(set, get, chunk, useTranscriptionServiceV2, context, source);
    }
    const { id, recordingId } = chunk;
    const { config, getIsLastChunk, waitForConnectivity } = get();
    const chunkInfo = { ...getChunkInfo(chunk) };

    try {
      set((s) => ({ processingChunksIds: [...s.processingChunksIds, id] }));
      logger.debug({ event_type: 'PROCESS_CHUNK_START', details: chunkInfo });

      await pRetry(
        async () => {
          context?.throwIfStale();
          await waitForConnectivity(context?.signal);
          context?.throwIfStale();
          if (context) {
            await config!.uploadChunk(chunk, useTranscriptionServiceV2, context.signal);
          } else {
            await config!.uploadChunk(chunk, useTranscriptionServiceV2);
          }
        },
        {
          retries: 4,
          shouldRetry: (error: FailedAttemptError) => {
            // do not retry upload if 404 is received, as session is hard deleted
            if (isSessionNotFoundError(error)) {
              return false;
            }

            return true;
          },
          onFailedAttempt: (err) => {
            logger.info({
              event_type: 'PROCESS_CHUNK_FAILED_ATTEMPT',
              details: {
                chunkInfo,
                ...getSafeErrorFields(err),
                attemptNumber: err.attemptNumber,
                retriesLeft: err.retriesLeft,
              },
            });
          },
        }
      );

      if (isAudioWorkEnabled(context)) {
        await simpleRetry(() => db.markChunkAsProcessed(chunk, !config!.retainData));
      }

      if (!isAudioWorkEnabled(context)) return;
      set((s) => ({ processingChunksIds: without(s.processingChunksIds, id) }));

      const isLastChunk = await getIsLastChunk(chunk);
      if (!isAudioWorkEnabled(context)) return;

      if (isLastChunk) {
        config!.onLastChunkProcessed?.(recordingId, useTranscriptionServiceV2);
        void get().cleanupRecordingsTask();
        set((s) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [recordingId]: _, ...rest } = s.chunkRecorders;
          return { chunkRecorders: rest };
        });
      }

      logger.debug({ event_type: 'PROCESS_CHUNK_DONE', details: chunkInfo });
    } catch (err) {
      if (!isAudioWorkEnabled(context)) return;
      set({ status: 'ERROR' });

      logger.error({
        event_type: 'PROCESS_CHUNK_FAILED',
        details: { ...getSafeErrorFields(err), chunkInfo },
      });

      if (isAudioWorkEnabled(context)) {
        await simpleRetry(() => db.markChunkAsProcessed(chunk, !config!.retainData));
      }
      if (isAudioWorkEnabled(context)) config!.onError?.(err);
    }
  },

  startChunkRecorder: (stream, useTranscriptionServiceV2, recordingKey) => {
    if (getPipelineMode() === 'ledger') {
      return startCaptureV2(set, get, stream, useTranscriptionServiceV2, recordingKey);
    }

    const { sessionId, recordingId } = recordingKey;
    const { getMicAnalyzer, getTabAnalyzer, releaseMicAnalyzer, releaseTabAnalyzer } =
      useAudioGraphStore.getState();
    const { id: micId, analyzer: micAnalyzer } = getMicAnalyzer();
    const { id: tabId, analyzer: tabAnalyzer } = getTabAnalyzer();

    const handleNewChunk = async (
      chunk: Chunk,
      isLastChunk: boolean,
      context: AudioWorkContext
    ) => {
      const sessionChunk = { sessionId, recordingId, ...chunk };
      logger.debug({ event_type: 'CHUNK_ADDED', details: getChunkInfo(sessionChunk) });

      try {
        context.throwIfStale();
        await simpleRetry(() => db.addChunk(sessionChunk));
        context.throwIfStale();
      } catch (err) {
        if (!context.isCurrent()) throw err;
        logger.error({
          event_type: 'ADD_CHUNK_ERROR',
          recording_id: recordingId,
          details: await getErrorInfo(err),
        });

        throw err;
      }

      if (isLastChunk) {
        context.throwIfStale();
        await simpleRetry(() => db.setRecordingLastChunkIndex(sessionChunk));
      }

      context.throwIfStale();
      // Main-parity: the upload is fire-and-forget from the recorder's view,
      // but tracked so the logout drain still waits for it.
      trackDetachedAudioWork(
        get().processChunk(sessionChunk, useTranscriptionServiceV2, context),
        'chunk-upload'
      );
    };

    const recorderProps: ConstructorParameters<typeof ChunkRecorder>[0] = {
      ...pick(get().config!, ['chunkSpec', 'format', 'codec']),
      stream,
      micAnalyzer,
      micAnalyzerId: micId,
      tabAnalyzer,
      tabAnalyzerId: tabId,
      onChunkAvailable: (c, l) =>
        runTrackedAudioWork((context) => handleNewChunk(c, l, context), 'other')
          .then(() => undefined)
          .catch(swallowAudioWorkCutoff),
      onError: (errorReason: string) => {
        logger.error({
          event_type: 'CHUNK_RECORDER_FATAL_ERROR',
          recording_id: recordingId,
          details: errorReason,
        });
        get().config!.onInterrupt?.();
      },
    };
    let chunkRecorder: ChunkRecorder;
    try {
      chunkRecorder = new ChunkRecorder(recorderProps);
    } catch (err) {
      releaseAnalyzerId(releaseMicAnalyzer, micId);
      if (tabId) releaseAnalyzerId(releaseTabAnalyzer, tabId);
      throw err;
    }

    set((state) => ({
      chunkRecorders: { ...state.chunkRecorders, [recordingId]: chunkRecorder },
    }));
  },

  stopChunkRecorder: (recordingId?: string) => {
    if (getPipelineMode() === 'ledger') return stopCaptureV2(set, get, recordingId);

    const expectedId = recordingId ?? get().activeRecordingKey?.recordingId;
    const chunkRecorder = get().chunkRecorders[expectedId!];

    if (chunkRecorder) {
      const { releaseMicAnalyzer, releaseTabAnalyzer } = useAudioGraphStore.getState();
      releaseMicAnalyzer(chunkRecorder.micAnalyzerId);
      if (chunkRecorder.tabAnalyzerId) releaseTabAnalyzer(chunkRecorder.tabAnalyzerId);
      return chunkRecorder.stop();
    } else {
      logger.error({
        event_type: 'STOP_CHUNK_RECORDER_NO_CHUNK_RECORDER_FOUND',
        recording_id: expectedId,
      });

      throw new Error('Unable to find correct chunk recorder to stop');
    }
  },

  // Stopped legacy recorders stay in their maps until their uploads finish, so
  // a failed start must take only its own entry. Discard and drop it before the
  // releases: a release that throws must not leave it capturing.
  discardChunkRecorder: (recordingId) => {
    const chunkRecorder = get().chunkRecorders[recordingId];
    if (chunkRecorder) {
      chunkRecorder.discard();
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [recordingId]: _, ...rest } = state.chunkRecorders;
        return { chunkRecorders: rest };
      });
      const { releaseMicAnalyzer, releaseTabAnalyzer } = useAudioGraphStore.getState();
      releaseMicAnalyzer(chunkRecorder.micAnalyzerId);
      if (chunkRecorder.tabAnalyzerId) releaseTabAnalyzer(chunkRecorder.tabAnalyzerId);
    }
    if (get().captureSlot?.recordingId === recordingId) discardCaptureSlotV2(set, get);
  },

  getIsLastChunk: async (chunk: SessionChunk) => {
    if (getPipelineMode() === 'ledger') return getIsLastChunkV2(get, chunk);
    try {
      const { sessionId, recordingId } = chunk;
      if (!get().chunkRecorders[recordingId]?.doneRecording) return false;
      const recording = await db.getRecording(sessionId, recordingId);
      if (!recording) {
        logger.warn({
          event_type: 'IS_LAST_CHUNK_RECORDING_MISSING',
          recording_id: recordingId,
        });
        return false;
      }
      return recording.chunks.every((c) => c.isProcessed);
    } catch {
      return false;
    }
  },
});
